import { getApiEndpoints } from '../api/endpoints';
import type {
  BusinessSignupRequest,
  BusinessSignupResponse,
  BusinessVerifyRequest,
  BusinessVerifyResponse,
  BusinessSignInRequest,
  BusinessSignInResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  BusinessAuthEnvelope,
  TokenResponse,
} from '@/features/auth/authTypes';
import type {
  ConfirmForgotPasswordRequest,
  ForgotPasswordRequest,
  PasswordResetResponse,
} from '@/features/password-reset';
import type { ILogger } from '../log/types';
import type { IAuthService } from './types';

type BusinessAuthResult<T> = {
  success: boolean;
  message: string;
  data: T | null;
  retryAfterSeconds?: number;
};

function parseRetryAfterSeconds(value: string | null): number | undefined {
  if (!value || !/^[1-9]\d*$/.test(value)) return undefined;

  const seconds = Number(value);
  return Number.isSafeInteger(seconds) ? seconds : undefined;
}

async function parseBusinessAuthResponse<T>(response: Response, fallback: string): Promise<BusinessAuthResult<T>> {
  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    return { success: false, message: 'Invalid response from server. Please try again.', data: null };
  }

  const envelope = (await response.json()) as BusinessAuthEnvelope<T>;
  const retryAfterSeconds = parseRetryAfterSeconds(response.headers.get('Retry-After'));
  return {
    success: response.ok,
    message: envelope.message || fallback,
    data: envelope.data ?? null,
    ...(retryAfterSeconds ? { retryAfterSeconds } : {}),
  };
}

export class AuthService implements IAuthService {
  logger: ILogger;

  constructor(logger: ILogger) {
    this.logger = logger;
  }

  async signup(request: BusinessSignupRequest): Promise<BusinessSignupResponse> {
    const endpoints = getApiEndpoints();
    const url = endpoints.auth.signup;

    this.logger.info('Signup request', { url, email: request.email });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const result = await parseBusinessAuthResponse<{ nextStep: 'CHECK_EMAIL' }>(response, 'Signup failed');
      this.logger.info('Signup response', { success: result.success, message: result.message });
      return result;
    } catch (error) {
      this.logger.error('Signup error', { error });

      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return {
          success: false,
          message: 'Network error. Please check your connection.',
          data: null,
        };
      }

      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        data: null,
      };
    }
  }

  async verifySignup(request: BusinessVerifyRequest): Promise<BusinessVerifyResponse> {
    const endpoints = getApiEndpoints();
    const url = endpoints.auth.confirmSignup;

    this.logger.info('Verify request', { url, email: request.email });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const result = await parseBusinessAuthResponse<{ nextStep: 'SIGN_IN' }>(response, 'Verification failed');
      this.logger.info('Verify response', { success: result.success, message: result.message });
      return result;
    } catch (error) {
      this.logger.error('Verify error', { error });

      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return {
          success: false,
          message: 'Network error. Please check your connection.',
          data: null,
        };
      }

      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        data: null,
      };
    }
  }

  async signIn(request: BusinessSignInRequest): Promise<BusinessSignInResponse> {
    const endpoints = getApiEndpoints();
    const url = endpoints.auth.signin;

    this.logger.info('Business sign-in request', { url, email: request.email });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const result = await parseBusinessAuthResponse<{ tokens: TokenResponse }>(response, 'Login failed');
      this.logger.info('Sign-in response', { success: result.success, message: result.message });
      return { ...result, tokens: result.data?.tokens };
    } catch (error) {
      this.logger.error('Sign-in error', { error });

      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return {
          success: false,
          message: 'Network error. Please check your connection.',
          data: null,
        };
      }

      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        data: null,
      };
    }
  }

  async refreshToken(request: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const endpoints = getApiEndpoints();
    const url = endpoints.auth.refresh;

    this.logger.info('Token refresh request', { url });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: request.refreshToken,
        }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        this.logger.error('Non-JSON response', { contentType });
        return {
          success: false,
          message: 'Invalid response from server. Please try again.',
        };
      }

      const data = await response.json();
      this.logger.info('Token refresh response', { success: data.success });

      if (!response.ok) {
        return {
          success: false,
          message: data.message || `Token refresh failed with status ${response.status}`,
          code: data.code,
        };
      }

      return {
        success: data.success,
        message: data.message || 'Token refreshed successfully',
        tokens: data.tokens,
        code: data.code,
      };
    } catch (error) {
      this.logger.error('Token refresh error', { error });

      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return {
          success: false,
          message: 'Network error. Please check your connection.',
        };
      }

      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
      };
    }
  }

  async forgotPassword(request: ForgotPasswordRequest): Promise<PasswordResetResponse> {
    const url = getApiEndpoints().auth.forgotPassword;
    this.logger.info('Password reset request', { url });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
      return await parseBusinessAuthResponse<{ nextStep: 'CHECK_EMAIL' }>(response, 'Password reset request failed');
    } catch (error) {
      this.logger.error('Password reset request failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return {
        success: false,
        message:
          error instanceof TypeError && error.message === 'Failed to fetch'
            ? 'Network error. Please check your connection.'
            : 'Unable to request a password reset. Please try again.',
        data: null,
      };
    }
  }

  async confirmForgotPassword(request: ConfirmForgotPasswordRequest): Promise<PasswordResetResponse> {
    const url = getApiEndpoints().auth.confirmForgotPassword;
    this.logger.info('Password reset confirmation request', { url });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
      return await parseBusinessAuthResponse<{ nextStep: 'SIGN_IN' }>(response, 'Password reset failed');
    } catch (error) {
      this.logger.error('Password reset confirmation failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return {
        success: false,
        message:
          error instanceof TypeError && error.message === 'Failed to fetch'
            ? 'Network error. Please check your connection.'
            : 'Unable to reset the password. Please try again.',
        data: null,
      };
    }
  }

  async resendVerification(request: ResendVerificationRequest): Promise<ResendVerificationResponse> {
    const endpoints = getApiEndpoints();
    const url = endpoints.auth.resendVerification;

    this.logger.info('Resend verification request', { url, email: request.email });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const result = await parseBusinessAuthResponse<{ nextStep: 'CHECK_EMAIL' }>(
        response,
        'Resend verification failed',
      );
      this.logger.info('Resend verification response', { success: result.success, message: result.message });
      return result;
    } catch (error) {
      this.logger.error('Resend verification error', { error });

      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return {
          success: false,
          message: 'Network error. Please check your connection.',
          data: null,
        };
      }

      return {
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        data: null,
      };
    }
  }
}
